import { useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

import {
  setTableToExport,
  setLimit,
  setPage,
  setTotal,
  setColumnVisibility,
  setShowTransactionExportModal,
  setTransactionExportsTableFilters,
  setClearTransactionExportsTableFilters,
  setDefaultStartDate,
  setDefaultEndDate,
  setFiltersForExport,
} from '@Modal/reducers/transactionExports/transactionExportsTableSlice';

// Types
import { StoreInterface } from 'src/app/types';
import { FilterItemType } from '@Modal/reducers/types';
import { useApolloClient } from '@apollo/client';
import { ProviderExportsDocument, TableKeyEnum } from '@Src/generated.graphql';
import { useSnackbar } from 'notistack';
import { useExportTransactionsToExcelMutation } from '@Src/generated.graphql';

export const useTransactionExportsTable = () => {
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const [exportTransactionsToExcel] = useExportTransactionsToExcelMutation();

  const {
    page,
    limit,
    total,
    selectedFilters,
    columnVisibility,
    columnVisibilityOptions,
    transactionExportsTableFilters,
    tableToExport,
    filtersForExport,
    showTransactionExportModal,
    defaultStartDate,
    defaultEndDate,
  } = useSelector((state: StoreInterface) => state?.transactionExportsTable);

  const refetchTransactionExports = () => {
    apolloClient.refetchQueries({
      include: [ProviderExportsDocument],
    });
    enqueueSnackbar('Exports successfully refetched', {
      variant: 'success',
      autoHideDuration: 2000,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  };

  const transactionExportsListFilters = useMemo(() => {
    const columnVisibilityStrArr = Array.from(Object.values(columnVisibility)).map((filt) => filt);
    return columnVisibilityStrArr as string[];
  }, [columnVisibility]);

  const handleApplyColumnVisibility = (column: string[]) => {
    const newVisibleColumns = [...transactionExportsListFilters, ...column];
    if (transactionExportsListFilters.filter((col) => column.includes(col)).length > 0) {
      const filteredNewVisibleColumns = transactionExportsListFilters.filter(
        (col) => col !== column[0]
      );
      dispatch(setColumnVisibility(filteredNewVisibleColumns));
    } else {
      dispatch(setColumnVisibility(newVisibleColumns));
    }
  };

  const visiblityCustomizable = useMemo(
    () => ({
      value: columnVisibility,
      options: columnVisibilityOptions,
      apply: (value: string[]) => handleApplyColumnVisibility(value),
    }),
    [columnVisibility, columnVisibilityOptions]
  );

  const handleFilterSelect = (filter: FilterItemType) => {
    dispatch(setTransactionExportsTableFilters(filter));
  };

  const handleClearTransactionExportsTableFilters = () => {
    dispatch(setClearTransactionExportsTableFilters());
  };

  const openTransactionExportModal = (
    table: string,
    startDate?: string,
    endDate?: string,
    filters?: object
  ) => {
    dispatch(setTableToExport(table));
    dispatch(setShowTransactionExportModal(true));
    dispatch(setFiltersForExport(filters));
    startDate && dispatch(setDefaultStartDate(startDate));
    endDate && dispatch(setDefaultEndDate(endDate));
  };

  const closeTransactionExportModal = () => {
    dispatch(setTableToExport(null));
    dispatch(setShowTransactionExportModal(false));
    dispatch(setDefaultStartDate(null));
    dispatch(setDefaultEndDate(null));
    dispatch(setFiltersForExport(null));
  };

  const handleExportTransactionsToExcel = async (startDate: string, endDate: string) => {
    try {
      await exportTransactionsToExcel({
        variables: {
          input: {
            dateRange: { from: startDate, to: endDate },
            tableKey: tableToExport as TableKeyEnum,
            filters: filtersForExport,
          },
        },
      });
      enqueueSnackbar('Report created successfully. To download, visit exports tab.', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      closeTransactionExportModal();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export transactions';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  return {
    // State
    tableToExport,
    filtersForExport,
    page,
    total,
    limit,
    selectedFilters,
    columnVisibility,
    visiblityCustomizable,
    columnVisibilityOptions,
    showTransactionExportModal,
    transactionExportsListFilters,
    transactionExportsTableFilters,
    defaultStartDate,
    defaultEndDate,
    // Actions
    setLimit,
    setPage,
    setTotal,
    setTransactionExportsTableFilters,
    setClearTransactionExportsTableFilters,
    // Handlers
    handleFilterSelect,
    openTransactionExportModal,
    closeTransactionExportModal,
    handleApplyColumnVisibility,
    refetchTransactionExports,
    handleExportTransactionsToExcel,
    handleClearTransactionExportsTableFilters,
  };
};
